import { MouseEventHandler, ReactNode } from 'react';

import { Field, FieldProps } from 'react-final-form';

import { Autocomplete, AutocompleteProps } from '@cast/design-system';

import { useControlsGroupState } from './controls-state';
import { useRffFormState } from '../hooks';
import { showErrorOnChange } from '../utils';

type RffAutocompleteProps<
  OptionItem = unknown,
  OptionValue = OptionItem,
  SelectionType extends 'single' | 'multiple' | undefined = undefined
> = AutocompleteProps<OptionItem, OptionValue, SelectionType> & {
  name: string;
  label?: string;
  renderError?: (error: string | string[]) => ReactNode;
  fieldProps?: Partial<FieldProps<OptionValue, any>>;
  onMouseOver?: MouseEventHandler;
  onMouseLeave?: MouseEventHandler;
};

export const RffAutocomplete = <
  OptionItem,
  OptionValue = OptionItem,
  SelectionType extends 'single' | 'multiple' | undefined = undefined
>({
  renderError,
  fieldProps,
  label,
  ...autocompleteProps
}: RffAutocompleteProps<OptionItem, OptionValue, SelectionType>) => {
  const { mode } = useRffFormState();
  const { disabled, readOnly, name } = useControlsGroupState({
    disabled: autocompleteProps.disabled,
    readOnly: autocompleteProps.inputProps?.readOnly ?? mode === 'view',
    name: autocompleteProps.name,
  });

  return (
    <Field
      {...fieldProps}
      name={name || autocompleteProps.name}
      render={(props) => {
        const {
          input: { onBlur, onFocus, onChange, value },
          meta,
        } = props;
        const showError = showErrorOnChange(meta);
        return (
          <Autocomplete
            {...(autocompleteProps as AutocompleteProps)}
            value={value || null}
            onChange={(option: any, val: any) => {
              autocompleteProps.onChange?.(option, val);
              onChange(val);
            }}
            inputProps={{
              label,
              ...autocompleteProps.inputProps,
              readOnly,
              onBlur,
              onFocus,
              error: showError
                ? renderError
                  ? renderError(meta.error)
                  : meta.error
                : undefined,
              success: meta.touched && meta.valid,
              // Tooltip passes these for it's direct child
              onMouseOver: autocompleteProps.onMouseOver,
              onMouseLeave: autocompleteProps.onMouseLeave,
            }}
            disabled={disabled}
          />
        );
      }}
    />
  );
};
